import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../ampli';
import { postSellTractorSurveyDataToSpreadSheet } from '../../../api/sheet/postSellTractorSurveyDataToSpreadSheet';
import { MainHead } from '../../../components/common/head/MainHead';
import { SpinnerLoader } from '../../../components/common/loading/SpinnerLoader';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { flarelaneEventKeys } from '../../../constants/flarelaneEvent/flarelaneEvent.contant';
import { pageInfo, sellTractorPageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { TEMP_DATA_KEYS } from '../../../constants/tempData/tempData.contant';
import { usePopup } from '../../../hooks/popup/usePopup';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useAppDispatch, useAppSelector } from '../../../hooks/store/useStore';
import { useGetUserProfile } from '../../../hooks/user/useGetUserProfile';
import { useUserTempData } from '../../../hooks/userTempData/useUserTempData';
import { useNotification } from '../../../provider/ToastNotificationProvider';
import { userSellTractorSurveySlice } from '../../../store/userSellTractorSurveySlice';
import { createUserSellTractorTempData } from '../../../utils/creator/createUserSellTractorTempData.util';
import { postMessageToRN } from '../../../utils/RNCommunicator/postMessageToRN.util';

interface SellTractorSurveyPageWrapperProps {
  headerTitle: string;
  description?: string;
  children: JSX.Element;
  isLoading?: boolean;
  canConfirm: boolean;
  confirmHandler?: () => void;
  closeHandler?: () => void;
  isTractorRequiredInputEmpty?: boolean;
  confirmButtonText?: string;
  prevButtonText?: string;
  prevButtonHandler?: () => void;
  buttonTooltip?: JSX.Element;
  steps?: string[];
  currentStep?: number;
}

export const SellTractorSurveyPageWrapper = ({
  headerTitle,
  description,
  children,
  isLoading,
  canConfirm,
  confirmHandler,
  closeHandler,
  confirmButtonText = '다음',
  isTractorRequiredInputEmpty = false,
  prevButtonText = '이전',
  prevButtonHandler,
  buttonTooltip,
  steps,
  currentStep,
}: SellTractorSurveyPageWrapperProps) => {
  const location = useLocation();
  const Popup = usePopup();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openNotification } = useNotification();

  const [isCloseButtonClicked, setIsCloseButtonClicked] = useState<boolean>(false);
  const [isShowSaveDataModal, setIsShowSaveDataModal] = useState<boolean>(false);

  const userSellTractorSurveySelector = useAppSelector((state) => state.userSellTractorSurvey);
  const dispatch = useAppDispatch();
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;

  const { userProfile } = useGetUserProfile();

  const { getUserTempData, setUserTempData } = useUserTempData();

  const handleSurveyCancelButtonClick = () => {
    if (closeHandler) {
      closeHandler();
    }
    ampli.서베이취소버튼클릭({
      '서베이 이름': '회원 판매 트랙터',
      type: String(userSellTractorSurveySelector.machineType),
    });

    if (
      userSellTractorSurveySelector.sellTractorIdForEdit ||
      userSellTractorSurveySelector.machineType === 'combine' ||
      userSellTractorSurveySelector.machineType === 'transplanter' ||
      isTractorRequiredInputEmpty
    ) {
      setIsCloseButtonClicked(true);
    } else {
      setIsShowSaveDataModal(true);
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('userWantExitSellTractorSurvey', 'true');
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    }
  };

  useLayout({
    fixedHeader: (
      <>
        <MainHead backButton={{ isShow: false }} closeButton={{ closeHandler: handleSurveyCancelButtonClick }} />
        {steps && currentStep ? (
          <StepBarContainer>
            {steps.map((step, index) => {
              return <StepBarItem key={step} isCompleted={currentStep >= index + 1} />;
            })}
          </StepBarContainer>
        ) : null}
      </>
    ),
    fixedFooter: (
      <ButtonSection>
        <BlurredSection />
        {buttonTooltip && buttonTooltip}
        {prevButtonHandler && (
          <PrevButton
            type={'button'}
            disabled={isLoading}
            onClick={() => {
              if (prevButtonHandler) {
                ampli.pvmClickSellTractorPrevButton({ title: headerTitle });
                ReactGA.event('pvm_click_sell_tractor_prev_button', { title: headerTitle });
                prevButtonHandler();
              }
            }}
          >
            <PrevButtonText>{prevButtonText}</PrevButtonText>
          </PrevButton>
        )}

        <ConfirmButton
          id={confirmButtonText === '완료' ? 'sell-tractor-complete' : ''}
          type={'button'}
          disabled={!canConfirm || isLoading}
          onClick={() => {
            if (confirmHandler) {
              if (confirmButtonText !== '완료') {
                ampli.pvmClickSellTractorNextButton({
                  title: headerTitle,
                  type: userSellTractorSurveySelector.machineType ?? '',
                  status: userSellTractorSurveySelector.sellTractorIdForEdit ? 'edit' : 'new',
                });
                ReactGA.event('pvm_click_sell_tractor_next_button', { title: headerTitle });
              }
              confirmHandler();
            }
          }}
        >
          <SpinnerLoader isVisible={isLoading ?? false} />
          {!isLoading && <ConfirmButtonText>{confirmButtonText}</ConfirmButtonText>}
        </ConfirmButton>
      </ButtonSection>
    ),
    fixedHeaderHeight: 82,
    fixedFooterHeight: 72,
    shouldShowFooter: false,
  });

  useEffect(() => {
    const isPageAllowedWithoutBrand =
      location.pathname !== sellTractorPageInfo.overviewPage.path &&
      location.pathname !== sellTractorPageInfo.locationPage.path;
    if (isPageAllowedWithoutBrand && !userSellTractorSurveySelector.brand) {
      openNotification({ message: '비정상적인 접근입니다.' });
      navigate('/survey/entry/sell-tractor', { replace: true });
    }
  }, [location.pathname, userSellTractorSurveySelector.brand]);

  useEffect(() => {
    const machineType = searchParams.get('type');

    if (!machineType && userSellTractorSurveySelector.machineType) {
      searchParams.set('type', userSellTractorSurveySelector.machineType);
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, userSellTractorSurveySelector.machineType]);

  return (
    <>
      <Container>
        <TopSection>
          <HeaderSection>
            <HeaderContainer>
              <Header>{headerTitle}</Header>
              {description && <Description>{description}</Description>}
            </HeaderContainer>
          </HeaderSection>

          {children}
        </TopSection>
      </Container>

      <PopupConfirmModal
        isShow={isCloseButtonClicked}
        hasCloseButton={false}
        closeModal={() => setIsCloseButtonClicked(false)}
        description={'작성한 내용을 잃게 돼요!\n그래도 중단하고 나가시겠어요?'}
        buttonArray={[
          {
            text: '계속 작성하기',
            onClick: () => setIsCloseButtonClicked(false),
            color: 'blue',
          },
          {
            text: '처음으로',
            onClick: () => {
              const redirectUrl = userSellTractorSurveySelector.sellTractorIdForEdit
                ? pageInfo.myInfoSellTractorListPage.path
                : pageInfo.sellTractorEntryPage.path;
              dispatch(userSellTractorSurveyActions.resetState());
              navigate(redirectUrl, { replace: true });
            },
            color: 'gray',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isShowSaveDataModal}
        hasCloseButton={false}
        closeModal={() => setIsShowSaveDataModal(false)}
        title={'정말 종료 하시겠어요?'}
        description={'나머지 내용은 다음에 이어서\n계속 작성 하실 수 있어요!'}
        buttonArray={[
          {
            text: '저장하고 나가기',
            onClick: () => {
              const { newSellTractorTempData } = createUserSellTractorTempData({
                formData: userSellTractorSurveySelector,
                isUserSubmit: false,
              });

              const { userTempData } = getUserTempData();
              userTempData.set(TEMP_DATA_KEYS.SELL_TRACTOR_SURVEY, newSellTractorTempData);
              setUserTempData({ newTempData: userTempData });

              if (userProfile) {
                postSellTractorSurveyDataToSpreadSheet({
                  userId: userProfile.id,
                  userName: userProfile.name,
                  userPhoneNumber: userProfile.phoneNumber,
                  completed: false,
                });
              }

              if (window.ReactNativeWebView) {
                postMessageToRN({
                  messageKey: 'flarelane_trackEvent',
                  messageParam: flarelaneEventKeys.pvm_sell_tractor_incomplete,
                });
              }
              ampli.pvmClickSellTractorExitWithSave();
              ReactGA.event('pvm_click_sell_tractor_exit_with_save');
              navigate(pageInfo.sellTractorEntryPage.path, { replace: true });
            },
            color: 'blue',
          },
          {
            text: '계속 작성하기',
            onClick: () => {
              ampli.pvmClickSellTractorContinueWriting();
              ReactGA.event('pvm_click_sell_tractor_continue_writing');
              setIsShowSaveDataModal(false);
            },
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  padding-bottom: 40px;
`;

const TopSection = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  height: 100%;

  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

const HeaderSection = styled.div`
  display: flex;
  padding: 24px 0px;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
`;

const Header = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */

  word-break: break-all;
`;

const Description = styled.pre`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const ButtonSection = styled.div`
  position: relative;
  display: flex;
  width: 100%;

  padding: 0px 16px 32px 16px;
  background-color: white;

  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;

const Button = styled.button`
  width: 100%;
  height: 40px;
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;

  gap: 4px;

  border-radius: var(--radius-medium, 8px);

  &:disabled {
    opacity: 0.3;
  }

  cursor: pointer;
`;

const ButtonText = styled.span`
  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const ConfirmButton = styled(Button)`
  background: var(--Light-Scheme-Color-primary-blue, #196eff);
`;

const ConfirmButtonText = styled(ButtonText)`
  color: var(--Light-Scheme-Monochrome-white, #fff);
`;

const PrevButton = styled(Button)`
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const PrevButtonText = styled(ButtonText)`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
`;

const StepBarContainer = styled.div`
  display: flex;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const StepBarItem = styled.div<{ isCompleted?: boolean }>`
  display: flex;
  height: 2px;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;

  border-radius: var(--radius-small, 4px);
  background: ${(props) => (props.isCompleted ? '#505363' : '#e4e4ed')};
`;
